import { useEffect, useState } from 'react';
import {
  CHIP_DEFS,
  SORT_OPTIONS,
} from '../../materiels/materielParcFilters';
import {
  DIAGNOSTIC_LABELS,
  LIFECYCLE_LABELS,
  TYPE_LABELS,
} from '../../materiels/materielDisplayMaps';

const LIFECYCLE_KEYS = ['EN_STOCK', 'AFFECTE', 'RETOUR', 'HORS_SERVICE'];
const TYPE_KEYS = [
  'PRINTER_CLOUD',
  'PRINTER_LOCAL',
  'TABLET',
  'POS',
  'DISPLAY',
  'SCANNER',
  'PAYMENT_TERMINAL',
  'OTHER',
];
const DIAG_KEYS = [
  { value: 'OK', label: DIAGNOSTIC_LABELS.OK },
  { value: 'CHECK', label: DIAGNOSTIC_LABELS.CHECK },
  { value: 'ERROR', label: DIAGNOSTIC_LABELS.ERROR },
];

/**
 * Recherche + selects + chips + tri — Parc Matériels V2.
 */
export default function MaterielFiltersBar({
  filters,
  searchInput,
  onSearchInputChange,
  onFiltersChange,
  onChip,
  onReset,
  onRefresh,
}) {
  const [localSearch, setLocalSearch] = useState(searchInput || '');

  useEffect(() => {
    setLocalSearch(searchInput || '');
  }, [searchInput]);

  // Debounce ~200 ms vers le parent
  useEffect(() => {
    const t = setTimeout(() => {
      if ((searchInput || '') !== localSearch) {
        onSearchInputChange?.(localSearch);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [localSearch, searchInput, onSearchInputChange]);

  const patch = (partial) => {
    onFiltersChange?.({ ...filters, ...partial });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="search"
          placeholder="Rechercher (commerce, n° série, modèle…)"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md min-w-[240px] flex-1 max-w-md text-sm"
          aria-label="Rechercher dans le parc"
        />
        <select
          value={filters.lifecycle || ''}
          onChange={(e) =>
            patch({
              lifecycle: e.target.value,
              chip: e.target.value === 'AFFECTE'
                ? 'affectes'
                : e.target.value === 'EN_STOCK'
                  ? 'en_stock'
                  : filters.chip === 'a_traiter'
                    ? 'a_traiter'
                    : 'all',
            })
          }
          className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
          aria-label="Filtrer par cycle de vie"
        >
          <option value="">Tous les cycles de vie</option>
          {LIFECYCLE_KEYS.map((k) => (
            <option key={k} value={k}>
              {LIFECYCLE_LABELS[k]}
            </option>
          ))}
        </select>
        <select
          value={filters.type === 'PRINTERS' ? 'PRINTERS' : filters.type || ''}
          onChange={(e) => {
            const v = e.target.value;
            patch({
              type: v,
              chip: v === 'PRINTERS' ? 'printers' : filters.chip === 'printers' ? 'all' : filters.chip,
            });
          }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
          aria-label="Filtrer par type"
        >
          <option value="">Tous les types</option>
          <option value="PRINTERS">Imprimantes (cloud + locale)</option>
          {TYPE_KEYS.map((k) => (
            <option key={k} value={k}>
              {TYPE_LABELS[k]}
            </option>
          ))}
        </select>
        <select
          value={filters.diagnostic || ''}
          onChange={(e) => patch({ diagnostic: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
          aria-label="Filtrer par diagnostic"
        >
          <option value="">Tous les diagnostics</option>
          {DIAG_KEYS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <select
          value={filters.sort || 'activity_desc'}
          onChange={(e) => patch({ sort: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
          aria-label="Trier la liste"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              Tri · {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onReset}
          className="text-sm text-gray-600 hover:text-gray-900 hover:underline px-1"
        >
          Réinitialiser
        </button>
        {typeof onRefresh === 'function' ? (
          <button
            type="button"
            onClick={onRefresh}
            className="text-sm text-primary-700 hover:underline px-1"
          >
            Rafraîchir
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filtres rapides">
        {CHIP_DEFS.map((chip) => {
          const active = (filters.chip || 'all') === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              onClick={() => onChip?.(chip.id)}
              aria-pressed={active}
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-primary-300'
              }`}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
